/* eslint-disable vue/valid-v-slot */
<template>
    <main class="quotes-page">
        <v-container class="quotes-page__container">
            <account-settings-header
                :items="links"
                title="Saved quotes"
                subtitle="Review quotes for repair and upgrade requests, then continue where you left off."
                :icon="mdiClipboardTextOutline"
            />

            <header class="quotes-page__heading">
                <div class="quotes-page__heading-copy">
                    <span class="quotes-page__eyebrow">YOUR ACCOUNT / SERVICE</span>
                    <h2>RMA &amp; upgrade quotes</h2>
                    <p>Quotes remain available for 30 days and close automatically afterward.</p>
                </div>
                <span
                    v-if="serverItemsLength !== null && typeof serverItemsLength !== 'undefined'"
                    class="quotes-page__count"
                >
                    {{ serverItemsLength }} saved {{ serverItemsLength === 1 ? 'quote' : 'quotes' }}
                </span>
            </header>

            <section class="quotes-card" aria-label="Saved quotes">
                <v-data-table
                    class="quotes-table"
                    dark
                    :server-items-length="serverItemsLength"
                    :loading="serverItemsLoading"
                    :items="displayedTableItems"
                    :options.sync="options"
                    :footer-props="footerOptions"
                    item-key="uid"
                    :item-class="quoteRowClass"
                    :headers="headers"
                    no-data-text="No saved quotes are available."
                    no-results-text="No saved quotes are available."
                    :items-per-page="5"
                    @update:options="lookForMoreServerItems"
                >
                    <template v-slot:item.selection="{ item }">
                        <input
                            v-model="selected"
                            class="quotes-table__radio"
                            type="radio"
                            name="saved-quote-selection"
                            :value="item"
                            :aria-label="`Select quote ${item.uid}`"
                            @click.stop
                        />
                    </template>
                    <template v-slot:item.total="{ value }">
                        {{ formatTotal(value) }}
                    </template>
                    <template v-slot:item.createdAt="{ value }">
                        {{ formatQuoteDate(value) }}
                    </template>
                    <template v-slot:item.problem="{ item }">
                        <span :title="problemFor(item)">{{ problemFor(item) }}</span>
                    </template>
                </v-data-table>
                <footer class="quotes-selection-bar" aria-live="polite">
                    <div class="quotes-selection-bar__copy">
                        <span class="quotes-selection-bar__eyebrow">NEXT STEP</span>
                        <strong v-if="selected">Quote {{ selected.uid }} selected</strong>
                        <span v-else>Select a quote to continue your repair or upgrade request.</span>
                    </div>
                    <v-btn class="quotes-table__continue" :disabled="!selected" depressed @click="continueRma">
                        Continue quote
                        <v-icon right>{{ mdiArrowRight }}</v-icon>
                    </v-btn>
                </footer>
            </section>
        </v-container>
    </main>
</template>

<script>
import Vue from 'vue'
import { relayPagination } from '@/mixins/relay_pagination.js'
import { navLinks } from '../navigation.js'
import { toString } from '@/utils'
import { gql } from '@apollo/client'
import { mdiArrowRight, mdiClipboardTextOutline } from '@mdi/js'
import AccountSettingsHeader from '../account_settings_header.vue'

export default Vue.extend({
    metaInfo: { title: `Your Account`, subTitle: `Account Settings` },
    name: `MyQuotes`,
    components: { AccountSettingsHeader },
    mixins: [relayPagination('savedQuoteConnection')],
    apollo: {
        savedQuoteConnection: {
            query: gql`
                query savedQuoteConnection {
                    savedQuoteConnection(service: "rma-manager") {
                        totalCount
                        pageInfo {
                            hasPreviousPage
                            hasNextPage
                            startCursor
                            endCursor
                        }
                        edges {
                            node {
                                uid
                                customerSaved
                                service
                                reference
                                externalResource
                                shipping {
                                    address {
                                        line1
                                        line2
                                        city
                                        state
                                        country
                                        postalCode
                                        international
                                        apo
                                    }
                                    contact {
                                        emailAddress
                                        phoneNumber
                                        firstName
                                        lastName
                                        companyName
                                        fullName
                                    }
                                }
                                billing {
                                    address {
                                        line1
                                        line2
                                        city
                                        state
                                        country
                                        postalCode
                                        international
                                        apo
                                    }
                                    contact {
                                        emailAddress
                                        phoneNumber
                                        firstName
                                        lastName
                                        companyName
                                        fullName
                                    }
                                }
                                items {
                                    uid
                                    name
                                    price
                                    quantity
                                    tax
                                    total
                                }
                                amount {
                                    freight
                                    handling
                                    discount
                                    tax
                                }
                                redirects {
                                    completed
                                    canceled
                                }
                                details {
                                    payment {
                                        uid
                                        source
                                        url
                                        status
                                        transaction
                                        adminStatus
                                        possibleStatusList
                                    }
                                    tax {
                                        uid
                                        source
                                        status
                                    }
                                }
                                createdAt
                                updatedAt
                                customerPo {
                                    poNumber
                                    poFileId
                                }
                                createdBy
                                subtotal
                                total
                            }
                        }
                    }
                }
            `
        }
    },
    data() {
        return {
            mdiClipboardTextOutline,
            mdiArrowRight,
            links: navLinks,
            selected: null,
            headers: [
                {
                    text: 'Select',
                    value: 'selection',
                    sortable: false,
                    width: 64,
                    cellClass: 'quotes-table__selection-cell',
                    align: 'center'
                },
                {
                    text: 'Quote #',
                    align: 'start',
                    sortable: false,
                    value: 'uid'
                },
                {
                    text: 'Serial Number',
                    value: 'externalResource.serial',
                    sortable: false
                },
                { text: 'Date Created', value: 'createdAt', sortable: false },
                {
                    text: 'Problem Reported',
                    value: 'externalResource.rmaInput.problem',
                    sortable: false
                },
                { text: 'Quote Total', value: 'total', sortable: false }
            ],
            footerOptions: {
                itemsPerPageOptions: [10, 25, 50]
            },
            options: {
                sortBy: [`createdAt`],
                sortDesc: [true],
                itemsPerPage: 10,
                page: 1
            }
        }
    },
    methods: {
        isQuoteSelected(item) {
            return this.selected !== null && this.selected.uid === item.uid
        },
        quoteRowClass(item) {
            return this.isQuoteSelected(item) ? 'quotes-table__row--selected' : ''
        },
        formatTotal(value) {
            const total = Number(value)
            return Number.isFinite(total) ? `$${total.toFixed(2)}` : '—'
        },
        formatQuoteDate(value) {
            if (!value) return '—'
            const date = new Date(value)
            return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString()
        },
        problemFor(item) {
            const input = item?.externalResource?.rmaInput || {}
            return [input.primaryFault, input.problem].filter(Boolean).join(' · ') || '—'
        },
        continueRma() {
            if (!this.selected || !this.selected.externalResource?.rmaInput) return
            const routeName =
                this.selected.externalResource.rmaInput.primaryFault === 'UPGRADE' ? 'upgrade-device' : 'create-rma'
            const toPage = routeName.includes('upgrade') ? 5 : 4
            this.$router.push({
                name: routeName,
                params: {
                    serial: this.selected.externalResource.serial,
                    toPage,
                    quoteData: this.selected
                },
                query: {
                    quoteId: this.selected.uid,
                    prefill: toString(this.selected.externalResource)
                }
            })
        },
        payQuote() {
            const routeName =
                this.selected.externalResource.rmaInput.primaryFault === 'UPGRADE' ? 'upgrade-device' : 'create-rma'
            const toPage = routeName.includes('upgrade') ? 5 : 4
            this.$router.push({
                name: routeName,
                params: { toPage, quoteData: this.sampleQuotes[0] }
            })
        }
    }
})
</script>

<style scoped>
.quotes-page {
    position: relative;
    isolation: isolate;
    min-height: 100vh;
    padding: 34px 0 54px;
    color: #f2f4fa;
    background: radial-gradient(ellipse at 88% 0, rgba(219, 81, 32, 0.15), transparent 38rem),
        linear-gradient(145deg, #292c35, #22252d 65%, #2a2d35);
    font-family: 'Inter Tight', 'Roboto', sans-serif;
}
.quotes-page::before {
    position: fixed;
    z-index: 0;
    inset: 0;
    background-image: url('https://storage.googleapis.com/gusa-assets/army-BG3.png');
    background-position: center;
    background-size: cover;
    content: '';
    filter: grayscale(1) brightness(0.8);
    mix-blend-mode: soft-light;
    opacity: 0.1;
    pointer-events: none;
}
.quotes-page__container {
    position: relative;
    z-index: 1;
    max-width: 1260px;
}
.quotes-page__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    margin: 0 0 20px;
    padding: 3px 2px 0;
}
.quotes-page__heading-copy {
    position: relative;
    padding-left: 17px;
}
.quotes-page__heading-copy::before {
    position: absolute;
    top: 2px;
    bottom: 3px;
    left: 0;
    width: 3px;
    background: #db5120;
    border-radius: 3px;
    content: '';
}
.quotes-page__eyebrow {
    color: #ff9a74;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1.4px;
}
.quotes-page__heading h2 {
    margin: 5px 0 4px;
    color: #f4f5f8;
    font-size: clamp(24px, 3vw, 32px);
    font-weight: 600;
    letter-spacing: -0.75px;
}
.quotes-page__heading p {
    margin: 0;
    color: #aeb5c3;
    font-size: 12px;
    line-height: 1.5;
}
.quotes-page__count {
    display: inline-flex;
    min-height: 34px;
    align-items: center;
    padding: 0 10px;
    color: #c7cbd4;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
}
.quotes-card {
    overflow: hidden;
    color: #f1f2f5;
    background: rgba(35, 38, 46, 0.88);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 12px;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.14);
}
.quotes-table {
    background: transparent !important;
    font-family: 'Inter Tight', 'Roboto', sans-serif !important;
}
.quotes-table ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
    background: #22252d;
    scrollbar-color: #f0784c #20222a;
    scrollbar-width: thin;
}
.quotes-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar {
    width: 9px;
    height: 9px;
}
.quotes-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-track {
    background: #20222a;
}
.quotes-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #f0784c, #c54820);
    border: 2px solid #20222a;
    border-radius: 9px;
}
.quotes-table ::v-deep table {
    min-width: 960px;
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0;
    font-family: 'Inter Tight', 'Roboto', sans-serif !important;
}
.quotes-table ::v-deep thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    height: 46px;
    padding: 0 14px;
    color: #d6d9e1 !important;
    background: #30333b;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12) !important;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.05px;
    text-align: left;
    white-space: nowrap;
}
.quotes-table ::v-deep tbody td {
    height: 52px;
    padding: 10px 14px;
    overflow: hidden;
    color: #e2e5ec !important;
    background: #282b33;
    border-bottom: 1px solid rgba(255, 255, 255, 0.055) !important;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
    font-size: 11px;
    font-weight: 400;
    line-height: 1.5;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.quotes-table ::v-deep tbody td + td {
    box-shadow: inset 1px 0 rgba(255, 255, 255, 0.025);
}
.quotes-table ::v-deep tbody tr:nth-child(even) td {
    background: #252830;
}
.quotes-table ::v-deep tbody tr:hover td {
    background: rgba(219, 81, 32, 0.12) !important;
}
.quotes-table ::v-deep tbody tr.quotes-table__row--selected td {
    background: #3d2d2a !important;
}
.quotes-table ::v-deep tbody tr.quotes-table__row--selected td:first-child {
    box-shadow: inset 3px 0 #f0784c;
}
.quotes-table ::v-deep tbody td.quotes-table__selection-cell {
    overflow: visible;
    padding: 0 8px !important;
    text-align: center;
}
.quotes-table__radio {
    width: 18px;
    height: 18px;
    margin: 0;
    accent-color: #f0784c;
    cursor: pointer;
}
.quotes-table__radio:focus-visible {
    outline: 2px solid #ffb396;
    outline-offset: 3px;
    border-radius: 50%;
}
.quotes-table ::v-deep .v-data-footer {
    min-height: 52px;
    color: #aeb5c3;
    background: transparent;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    font-family: 'Inter Tight', 'Roboto', sans-serif;
    font-size: 10px;
}
.quotes-table ::v-deep .v-data-footer .v-icon {
    color: #c6cbd5;
}
.quotes-table ::v-deep .v-data-footer__select,
.quotes-table ::v-deep .v-data-footer__pagination {
    color: #aeb5c3;
}
.quotes-table ::v-deep .v-data-table-header th .v-icon {
    color: #ff9a74;
}
.quotes-table ::v-deep .v-data-footer__select .v-input__slot::before,
.quotes-table ::v-deep .v-data-footer__select .v-input__slot::after {
    display: none;
}
.quotes-table ::v-deep .v-data-table__empty-wrapper {
    height: 120px;
    color: #aeb5c3;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
}
.quotes-table ::v-deep a {
    color: #ff9a74;
}
.quotes-selection-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-height: 70px;
    padding: 12px 20px;
    background: linear-gradient(100deg, rgba(219, 81, 32, 0.075), rgba(19, 21, 27, 0.22) 55%);
    border-top: 1px solid rgba(255, 255, 255, 0.09);
}
.quotes-selection-bar__copy {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 3px;
    color: #aeb5c3;
    font-size: 11px;
}
.quotes-selection-bar__eyebrow {
    color: #f2a58b;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 1.1px;
}
.quotes-selection-bar__copy strong {
    color: #f2f4fa;
    font-size: 12px;
    font-weight: 600;
}
.quotes-table__continue.v-btn {
    min-height: 40px;
    padding: 0 16px;
    color: #fff;
    background: linear-gradient(125deg, #e76638, #db5120);
    border: 1px solid rgba(255, 185, 154, 0.25);
    border-radius: 7px;
    box-shadow: 0 5px 14px rgba(219, 81, 32, 0.18);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1px;
    text-transform: none;
    transition: transform 140ms ease, box-shadow 140ms ease;
}
.quotes-table__continue.v-btn:not(.v-btn--disabled):hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(219, 81, 32, 0.28);
}
.quotes-table__continue.v-btn:disabled {
    color: #9299a8 !important;
    background: rgba(255, 255, 255, 0.055) !important;
    border-color: rgba(255, 255, 255, 0.1);
    box-shadow: none;
}
.quotes-table__continue.v-btn::before {
    opacity: 0 !important;
}
.quotes-table__continue.v-btn:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 3px;
}
@media (max-width: 600px) {
    .quotes-page {
        padding-top: 20px;
    }
    .quotes-page__heading {
        align-items: flex-start;
        flex-direction: column;
        gap: 12px;
        margin-bottom: 15px;
    }
    .quotes-selection-bar {
        align-items: stretch;
        flex-direction: column;
        padding: 13px 15px;
    }
    .quotes-selection-bar .v-btn {
        width: 100%;
    }
}
</style>
